import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ContentTemplate } from 'web-library';
import { MutationStatus } from 'react-query';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import RouteKey from '../../../../routes/key';
import UploadSupportingDocumentPassportPhoto from './UploadSupportingDocumentPassportPhoto';
import {
  ApplicationType,
  NonNationalExamType,
  UploadDocumentType,
} from '../../../../api/candidate/config/model';
import { usePostUploadPresignedUrl } from '../../../../api-refactored/special-consideration/usePostUploadPresignedUrl';
import { PresignedUrlResponse } from '../../../../models/PresignedUrl';
import { prepareFiles } from '../../../../utils/UploadUtil';
import { usePostPhotoUploadValidate } from '../../../../api-refactored/non-national-exam-registration/usePostPhotoUploadValidate';
import { useGetDocumentUploadStatus } from '../../../../api-refactored/document/useGetDocumentUploadStatus';
import { FileUploadState } from '../../../../models/File';
import {
  UploadedFileInfoResponse,
  UploadedFileModel,
  useGetUploadedFileInfo,
} from '../../../../api-refactored/non-national-exam-registration/useGetUploadedFileInfo';
import { usePostValidateSupportingDocumentFileSize } from '../../../../api-refactored/non-national-exam-registration/usePostValidateSupportingDocumentFileSize';
import NonNationalUpperUploadContent from './NonNationalUpperUploadContent';
import NonNationalUploadButtons from './NonNationalUploadButtons';
import { ParentParticularsLocation } from '../../../../pages/examRegistration/NonNationalExam/AEIS/ParentParticulars';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import { existInArray } from '../../../../utils/ArrayUtils';
import { NonNationalExamNavigationContext } from '../../../../routes/ExamRoute';
import { useGetPassportDisabled } from '../../../../api-refactored/non-national-exam-registration/useGetPassportDisabled';
import { NonNexPassportDisableContext } from '../../../../routes/RootRouter';

const { confirmParticulars } = RouteKey.exam.child;

interface NonNationalUploadDocumentProps {
  isAEIS: boolean;
  isOpsAdmin?: boolean;
}

const applicationType = (isAEIS: boolean) =>
  isAEIS ? NonNationalExamType.AEIS : NonNationalExamType.SPE;

const createDraftFilePhoto = (
  supportingDocuments: UploadedFileModel[],
  passportPhoto: UploadedFileModel,
  setDraftFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>,
  setDraftPhoto: React.Dispatch<React.SetStateAction<UploadedFile[]>>
) => {
  if (supportingDocuments) {
    const attachFiles = supportingDocuments.map((f: UploadedFileModel) => {
      const newFile = new File([new ArrayBuffer(f.fileSizeInBytes)], f.fileName);
      return {
        file: newFile,
        hasError: false,
        pending: false,
        uploaded: true,
        key: f.fileUuid,
      };
    });
    setDraftFiles(attachFiles);
  }
  if (passportPhoto) {
    const newPhoto = new File(
      [new ArrayBuffer(passportPhoto.fileSizeInBytes)],
      passportPhoto.fileName
    );
    const attachPhoto = [
      {
        file: newPhoto,
        hasError: false,
        pending: false,
        uploaded: true,
        key: passportPhoto.fileUuid,
      },
    ];
    setDraftPhoto(attachPhoto);
  }
};

export const handleApiButtonStatus = (
  isAEIS: boolean,
  draftFiles: UploadedFile[],
  files: UploadedFile[],
  draftPhoto: UploadedFile[],
  photo: UploadedFile[],
  setIsDisableApiButton: React.Dispatch<React.SetStateAction<boolean>>,
  isPassportDisabled: boolean,
  isFileRejected: boolean = false
) => {
  setIsDisableApiButton(true);

  if (isFileRejected) {
    setIsDisableApiButton(true);
    return;
  }

  if (isAEIS) {
    if (isPassportDisabled && (files.length || draftFiles.length)) {
      setIsDisableApiButton(false);
    } else {
      if (files.length && photo.length) {
        setIsDisableApiButton(false);
      }
      if (files.length && draftPhoto.length) {
        setIsDisableApiButton(false);
      }
      if (draftFiles.length && photo.length) {
        setIsDisableApiButton(false);
      }
      if (draftFiles.length && draftPhoto.length) {
        setIsDisableApiButton(false);
      }
    }
  } else if (photo.length || draftPhoto.length) {
    setIsDisableApiButton(false);
  }
};

interface useNonNationalUploadDocumentProps {
  isAEIS: boolean;
  applicationUuid: string;
  nextPageUrl?: string;
  isOpsAdmin?: boolean;
  isPassportDisabled: boolean;
}

export const useNonNationalUploadDocument = ({
  isAEIS,
  applicationUuid,
  nextPageUrl,
  isOpsAdmin = false,
  isPassportDisabled,
}: useNonNationalUploadDocumentProps) => {
  const history = useHistory();
  const { state } = useLocation<ParentParticularsLocation>();
  const { t } = useTranslation();
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [photo, setPhoto] = useState<UploadedFile[]>([]);
  const [draftPhoto, setDraftPhoto] = useState<UploadedFile[]>([]);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const [isPhotoRejected, setIsPhotoRejected] = useState<boolean>(false);
  const [isFileRejected, setIsFileRejected] = useState<boolean>(false);
  const [photoRejectedErrorMessage, setPhotoRejectedErrorMessage] = useState<string>('');
  const [fileRejectedErrorMessage, setFileRejectedErrorMessage] = useState<string>('');
  const [isDisableApiButton, setIsDisableApiButton] = useState<boolean>(false);
  const [lockedByUser, setLockedByUser] = useState<string>();

  const [isPhotoUploaded, setIsPhotoUploaded] = useState<boolean>(false);
  const [isFilesUploaded, setIsFilesUploaded] = useState<boolean>(false);

  const [isFetchedDraft, setIsFetchedDraft] = useState<boolean>(false);

  const [timeoutRejectedErrorMessage, setTimeoutRejectedErrorMessage] = useState<string>('');

  const proceedToNextPage = useCallback(() => nextPageUrl && history.push(nextPageUrl, state), [
    history,
    nextPageUrl,
    state,
  ]);

  useEffect(() => {
    if (isPhotoUploaded && isFilesUploaded) {
      proceedToNextPage();
    }
  }, [isPhotoUploaded, isFilesUploaded, proceedToNextPage]);

  useEffect(() => {
    handleApiButtonStatus(
      isAEIS,
      draftFiles,
      files,
      draftPhoto,
      photo,
      setIsDisableApiButton,
      isPassportDisabled,
      isFileRejected
    );
  }, [
    draftFiles,
    draftFiles.length,
    draftPhoto,
    draftPhoto.length,
    files,
    files.length,
    isAEIS,
    photo,
    photo.length,
    isFileRejected,
  ]);

useEffect(() => {
  const allFileNames = [...files, ...draftFiles].map(f => f.file.name);
  const hasDuplicate = new Set(allFileNames).size !== allFileNames.length;

  if (hasDuplicate) {
    setIsFileRejected(true);
    setFileRejectedErrorMessage(t('exam->>UploadDocument.DuplicateFileNames'));
    setIsDisableApiButton(true);
  } else {
    setIsFileRejected(false);
    setFileRejectedErrorMessage('');
    handleApiButtonStatus(
      isAEIS,
      draftFiles,
      files,
      draftPhoto,
      photo,
      setIsDisableApiButton,
      isPassportDisabled,
      false
    );
  }
}, [files, draftFiles, draftPhoto, photo, t, isOpsAdmin, isAEIS, isPassportDisabled]);

  // CPEPOKPNEH-3250 Error message for photo upload not displaying properly
  useEffect(() => {
    if (photo.length > 0) {
      setIsPhotoRejected(false); // Reset error state when new photo is uploaded
    }
  }, [photo]);

  useEffect(() => {
    if (photo.length && !isPhotoRejected) {
      setPhotoRejectedErrorMessage('');
    }
  }, [photo.length, isPhotoRejected]);

const mutatePostValidateSupportingDocumentFileSize = usePostValidateSupportingDocumentFileSize({
  onSuccess: () => {
    setIsFilesUploaded(true);
  }
});

  useEffect(() => {
    const isFilesUploading = files.some((file) => file.pending) && !timeoutRejectedErrorMessage;
    const isFilesFailed = files.some((file) => file.hasError);
    const isAllFilesUploaded = files.every((file) => file.uploaded);
    const fileKey = files.map((f: UploadedFile) => f.key);

    const isPhotoUploading = photo[0]?.pending && !timeoutRejectedErrorMessage;

    if (
      isFilesFailed ||
      timeoutRejectedErrorMessage.length ||
      (isAllFilesUploaded && isPhotoRejected && !isPhotoUploading)
    ) {
      setApiButtonStatus('idle');
    }
    if (isPhotoUploading || isFilesUploading) {
      setApiButtonStatus('loading');
    }
    if (
      files.length > 0 &&
      isAllFilesUploaded &&
      !isFilesUploading &&
      !isFilesFailed &&
      shouldSubmit
    ) {
      setApiButtonStatus('idle');
      mutatePostValidateSupportingDocumentFileSize.mutate({ uuidList: fileKey });
      setShouldSubmit(false);
    }
  }, [
    files,
    isPhotoRejected,
    mutatePostValidateSupportingDocumentFileSize,
    photo,
    shouldSubmit,
    timeoutRejectedErrorMessage,
  ]);

  const {
    mutate: mutatePostPhotoUploadValidate,
    status: statusPhotoUploadValidate,
  } = usePostPhotoUploadValidate({
    onSuccess: () => {
      if (isAEIS && ((draftFiles.length === 0 && !isOpsAdmin) || (isOpsAdmin && files.length))) {
        setIsPhotoUploaded(true);
      } else {
        proceedToNextPage();
      }
    },
    onError: (err) => {
      setIsPhotoRejected(true);
      if (!isFilesUploaded) {
        setApiButtonStatus('loading');
      } else {
        setApiButtonStatus('idle');
      }
      if (err.response?.data.message === 'Invalid photo dimension.') {
        if (files.length === 0) {
          setPhoto([]);
          refetchUploadedFileInfo().then(() => null);
        }
        setPhotoRejectedErrorMessage(t('exam->>UploadDocument.IncorrectPhotoSize'));
      } else {
        setPhotoRejectedErrorMessage(err.response?.data.message!);
      }
    },
  });

  const { refetch: refetchUploadedFileInfo } = useGetUploadedFileInfo({
    uuid: applicationUuid,
    onSuccess: (data: UploadedFileInfoResponse) => {
      const { supportingDocuments, passportPhoto } = data;
      createDraftFilePhoto(supportingDocuments, passportPhoto, setDraftFiles, setDraftPhoto);
      setIsFetchedDraft(true);
      setLockedByUser(data.lockedByUser);
    },
  });

  useEffect(() => {
    const isAllFilesUploaded = files.every((file) => file.uploaded);
    if (
      statusPhotoUploadValidate === 'error' &&
      isAllFilesUploaded &&
      isPhotoRejected &&
      files.length > 0 &&
      photo.length === 1
    ) {
      setFiles([]);
      setPhoto([]);
      refetchUploadedFileInfo().then(() => null);
    }
  }, [files, isPhotoRejected, photo.length, refetchUploadedFileInfo, statusPhotoUploadValidate]);

  const proceedToNextPageCondition =
    lockedByUser ||
    (draftPhoto.length && draftFiles.length && files.length === 0) ||
    (!isAEIS && draftPhoto.length) ||
    (isOpsAdmin &&
      isAEIS &&
      draftFiles.length === 0 &&
      files.length === 0 &&
      draftPhoto.length === 0 &&
      photo.length === 0) ||
    (isOpsAdmin && !isAEIS && draftPhoto.length === 0 && photo.length === 0) ||
    (isOpsAdmin && isAEIS && draftFiles.length && files.length === 0 && photo.length === 0) ||
    (isOpsAdmin && isAEIS && draftPhoto.length && files.length === 0) ||
    (isAEIS && isPassportDisabled && files.length === 0 && draftFiles.length && photo.length === 0);

  return {
    apiButtonStatus,
    isPhotoRejected,
    isFileRejected,
    isPhotoUploaded,
    isDisableApiButton,
    files,
    photo,
    draftFiles,
    draftPhoto,
    photoRejectedErrorMessage,
    fileRejectedErrorMessage,
    lockedByUser,
    setFiles,
    setPhoto,
    setDraftFiles,
    setDraftPhoto,
    setShouldSubmit,
    statusPhotoUploadValidate,
    mutatePostPhotoUploadValidate,
    setApiButtonStatus,
    setIsPhotoUploaded,
    proceedToNextPage,
    isFetchedDraft,
    setTimeoutRejectedErrorMessage,
    timeoutRejectedErrorMessage,
    proceedToNextPageCondition,
  };
};

const NonNationalUploadDocument = ({
  isAEIS,
  isOpsAdmin = false,
}: NonNationalUploadDocumentProps) => {
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();
  const [isPassportDisabled, setIsPassportDisabled] = useState<boolean>(false);
  const nonNexPassportDisableContext = useContext(NonNexPassportDisableContext);
  const {
    apiButtonStatus,
    isDisableApiButton,
    isPhotoRejected,
    isFileRejected,
    isPhotoUploaded,
    files,
    setFiles,
    draftFiles,
    setDraftFiles,
    photo,
    setPhoto,
    draftPhoto,
    setDraftPhoto,
    photoRejectedErrorMessage,
    fileRejectedErrorMessage,
    lockedByUser,
    setShouldSubmit,
    statusPhotoUploadValidate,
    mutatePostPhotoUploadValidate,
    setApiButtonStatus,
    setIsPhotoUploaded,
    proceedToNextPage,
    isFetchedDraft,
    setTimeoutRejectedErrorMessage,
    timeoutRejectedErrorMessage,
    proceedToNextPageCondition,
  } = useNonNationalUploadDocument({
    isAEIS,
    applicationUuid,
    nextPageUrl: confirmParticulars.route,
    isOpsAdmin,
    isPassportDisabled: nonNexPassportDisableContext.isPassportDisabled.valueOf(),
  });

  const { t } = useTranslation();
  const authorities = useGetUserAuthorities();
  const hasViewAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_VIEW)
    : true;

  const hasEditAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_EDIT)
    : true;
  const getUploadFilePresignedUrlMutation = usePostUploadPresignedUrl({
    onSuccess: (res: PresignedUrlResponse[]) => {
      const newFilesInfo: React.SetStateAction<UploadedFile[]> = [];
      files.forEach((_item: UploadedFile, index: number) => {
        const pendingFile = files[index];
        const fileData = res[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        newFilesInfo.push(pendingFile);
      });
      setFiles(newFilesInfo);
      setShouldSubmit(true);
    },
    onError: (err) => {
      setTimeoutRejectedErrorMessage(err?.response?.data?.message!);
    },
  });

  const getUploadPhotoPresignedUrlMutation = usePostUploadPresignedUrl({
    onSuccess: (res: PresignedUrlResponse[]) => {
      const newPhotoInfo: React.SetStateAction<UploadedFile[]> = [];
      photo.forEach((_item: UploadedFile, index: number) => {
        const pendingPhoto = photo[index];
        const photoData = res[index];
        pendingPhoto.presignedUrl = photoData.presignedUrl;
        pendingPhoto.key = photoData.key;
        newPhotoInfo.push(pendingPhoto);
      });
      setPhoto(newPhotoInfo);
    },
    onError: (err) => {
      setTimeoutRejectedErrorMessage(err?.response?.data?.message!);
    },
  });

  useGetDocumentUploadStatus({
    fileKey: photo[0]?.key as string,
    enabled:
      getUploadPhotoPresignedUrlMutation.status === 'success' &&
      statusPhotoUploadValidate === 'success' &&
      !isPhotoUploaded &&
      !isPhotoRejected &&
      !timeoutRejectedErrorMessage,
    refetchInterval: 100000,
    onSuccess: (response) => {
      if (response.fileStatus === FileUploadState.SUCCESS) {
        mutatePostPhotoUploadValidate({ uuid: photo[0]?.key as string });
      }
      if (response.fileStatus === FileUploadState.FAILED) {
        setApiButtonStatus('idle');
      }
      if (response.fileStatus === FileUploadState.TIMEOUT) {
        setApiButtonStatus('idle');
      }
    },
  });

  useGetPassportDisabled({
    onSuccess: (res) => {
      setIsPassportDisabled(true);
      console.log(res);
      nonNexPassportDisableContext.setIsPassportDisabled(res);
    },
  });

  const handleConfirmUpload = () => {
    setTimeoutRejectedErrorMessage('');

    if (!hasEditAccess && hasViewAccess) {
      proceedToNextPage();
      return;
    }
    setApiButtonStatus('loading');
    if (proceedToNextPageCondition) {
      proceedToNextPage();
      return;
    }
    if (
      isAEIS &&
      (isOpsAdmin || nonNexPassportDisableContext.isPassportDisabled || draftPhoto.length) &&
      photo.length === 0
    ) {
      setIsPhotoUploaded(true);
      const { filenames } = prepareFiles(files, setFiles);
      getUploadFilePresignedUrlMutation.mutate({
        uuid: applicationUuid,
        applicationType: ApplicationType.NON_NATIONAL_EXAM,
        filenames,
        uploadDocumentType: UploadDocumentType.SUPP_DOC,
      });
    }
    if ((isAEIS && (isOpsAdmin || draftFiles.length) && files.length === 0) || !isAEIS) {
      const { filenames: photonames } = prepareFiles(photo, setPhoto);
      getUploadPhotoPresignedUrlMutation.mutate({
        uuid: applicationUuid,
        applicationType: ApplicationType.NON_NATIONAL_EXAM,
        filenames: photonames,
        uploadDocumentType: UploadDocumentType.PASSPORT_PHOTO,
      });
    }
    if (isAEIS && files.length && photo.length && draftPhoto.length === 0) {
      const { filenames: photonames } = prepareFiles(photo, setPhoto);
      const { filenames } = prepareFiles(files, setFiles);
      getUploadPhotoPresignedUrlMutation.mutate({
        uuid: applicationUuid,
        applicationType: ApplicationType.NON_NATIONAL_EXAM,
        filenames: photonames,
        uploadDocumentType: UploadDocumentType.PASSPORT_PHOTO,
      });
      getUploadFilePresignedUrlMutation.mutate({
        uuid: applicationUuid,
        applicationType: ApplicationType.NON_NATIONAL_EXAM,
        filenames,
        uploadDocumentType: UploadDocumentType.SUPP_DOC,
      });
    }
  };

  return (
    <ContentTemplate isContinuable>
      <NonNationalUpperUploadContent
        isAEIS={isAEIS}
        files={files}
        setFiles={setFiles}
        applicationType={applicationType(isAEIS)}
        draftFiles={draftFiles}
        setDraftFiles={setDraftFiles}
        isLoading={apiButtonStatus === 'loading'}
        isFetchedDraft={isFetchedDraft}
        hasEditAccess={hasEditAccess}
        isPassportDisable={nonNexPassportDisableContext.isPassportDisabled.valueOf()}
      />
      {isPassportDisabled && !nonNexPassportDisableContext.isPassportDisabled && (
        <>
          <span id="upload-image-passport-size">
            <UploadSupportingDocumentPassportPhoto
              files={photo}
              setFiles={setPhoto}
              applicationType={isAEIS ? NonNationalExamType.AEIS : NonNationalExamType.SPE}
              draftFiles={draftPhoto}
              setDraftFiles={setDraftPhoto}
              uploadDocumentType={UploadDocumentType.PASSPORT_PHOTO}
              isLoading={apiButtonStatus === 'loading'}
              isFetchedDraft={isFetchedDraft}
              hasEditAccess={hasEditAccess}
            />
          </span>
        </>
      )}
      <NonNationalUploadButtons
        isPhotoRejected={isPhotoRejected}
        isFileRejected={isFileRejected}
        apiButtonStatus={apiButtonStatus}
        isDisableApiButton={isDisableApiButton}
        photoRejectedErrorMessage={photoRejectedErrorMessage}
        fileRejectedErrorMessage={fileRejectedErrorMessage}
        timeoutRejectedErrorMessage={timeoutRejectedErrorMessage}
        handleConfirmUpload={handleConfirmUpload}
        lockedByUser={lockedByUser}
        isOpsAdmin={isOpsAdmin}
      />
    </ContentTemplate>
  );
};
export default NonNationalUploadDocument;
